import { StateCreator } from "zustand";

import { DeliveryFile } from "models/DeliveryFile";
import { ArchiveItem } from "models/ArchiveItem";
import { DeliveryUploadFile } from "views/delivery/containers/DocumentDeliveryUpload";
import { RootState } from "store";
import { DeliveryAPI } from "api/delivery";

export interface DeliverySlice {
  fileMoving: boolean;
  filesMoving: boolean;
  filesUploading: boolean;
  isLoadingUploadedDocuments: boolean;
  uploadedDocuments: DeliveryFile[];

  gettingUploadedDocuments: () => void;
  getUploadedDocuments: (items: DeliveryFile[]) => void;
  failedToGetUploadedDocuments: () => void;
  setFilesUploading: (isUploading: boolean) => void;
  filesUploaded: (files: DeliveryFile[]) => void;
  setFileMoving: () => void;
  fileMoved: (
    fileId: string,
    handledById: string,
    handledByName: string,
    name: string
  ) => void;
  failedToUploadFiles: () => void;
  updateDeliveryFileName: (name: string, file: string) => void;
  setHandled: (
    itemToHandleId: string,
    handledById: string,
    handledByName: string
  ) => void;
  fetchUploadedFiles: (customerId: string) => void;
  setFileAsHandled: (itemToHandle: DeliveryFile, customerId: string) => void;
  moveToArchive: (
    item: ArchiveItem | DeliveryFile,
    customerId: string,
    path: string,
    name: string
  ) => void;
  sendFile: (
    file: DeliveryUploadFile,
    customerId: string,
    setFiles: React.Dispatch<React.SetStateAction<DeliveryUploadFile[]>>,
    setFailedWrapper: (isFailed: boolean, message: string) => boolean
  ) => void;
  deleteDocuments: (customerId: string, fileId: string) => void;
}

export const createDeliverySlice: StateCreator<
  RootState,
  [["zustand/devtools", never]],
  [],
  DeliverySlice
> = (set, get) => ({
  fileMoving: false,
  filesMoving: false,
  filesUploading: false,
  isLoadingUploadedDocuments: false,
  uploadedDocuments: [],

  gettingUploadedDocuments: () =>
    set(
      ({ delivery }) => ({
        delivery: {
          ...delivery,
          isLoadingUploadedDocuments: true
        }
      }),
      false,
      "delivery/UPLOADED_DOCUMENTS_LOADING"
    ),
  getUploadedDocuments: (items: DeliveryFile[]) =>
    set(
      ({ delivery }) => ({
        delivery: {
          ...delivery,
          uploadedDocuments: items,
          isLoadingUploadedDocuments: false
        }
      }),
      false,
      "delivery/UPLOADED_DOCUMENTS_LOADED"
    ),
  failedToGetUploadedDocuments: () =>
    set(
      ({ delivery }) => ({
        delivery: {
          ...delivery,
          isLoadingUploadedDocuments: false
        }
      }),
      false,
      "delivery/UPLOADED_DOCUMENTS_LOAD_FAILED"
    ),
  setFilesUploading: (isUploading: boolean) =>
    set(
      ({ delivery }) => ({
        delivery: {
          ...delivery,
          filesUploading: isUploading
        }
      }),
      false,
      "delivery/DELIVERY_FILES_UPLOADING"
    ),
  filesUploaded: (files: DeliveryFile[]) =>
    set(
      ({ delivery }) => {
        // The files upload backend route returns an array of files where
        // the size of the file is given by the property "size", while
        // the model we keep in frontend has the property "fileSize"
        const newUploadedDocuments = files.map((file: DeliveryFile) => ({
          ...file,
          fileSize: file.size || ""
        }));
        const uploadedDocuments = [
          ...delivery.uploadedDocuments,
          ...newUploadedDocuments
        ];

        return {
          delivery: {
            ...delivery,
            uploadedDocuments
          }
        };
      },
      false,
      "delivery/DELIVERY_FILES_UPLOADED"
    ),
  setFileMoving: () =>
    set(
      ({ delivery }) => ({
        delivery: {
          ...delivery,
          fileMoving: true
        }
      }),
      false,
      "delivery/DELIVERY_FILE_MOVING"
    ),
  fileMoved: (fileId: string, handledById: string, handledByName: string) =>
    set(
      ({ delivery }) => {
        const uploadedDocuments = [...delivery.uploadedDocuments];

        const files = uploadedDocuments.filter((f) => f.id === fileId);

        if (files.length > 0 && !files[0].handled) {
          Object.assign(files[0], {
            handled: true,
            handledAt: new Date(),
            handledById,
            handledByName
          });
        }

        return {
          delivery: {
            ...delivery,
            fileMoving: false,
            uploadedDocuments
          }
        };
      },
      false,
      "delivery/DELIVERY_FILE_MOVED"
    ),
  failedToUploadFiles: () =>
    set(
      ({ delivery }) => ({
        delivery: {
          ...delivery,
          filesMoving: false,
          filesUploading: false
        }
      }),
      false,
      "delivery/FILES_UPLOAD_FAILED"
    ),
  updateDeliveryFileName: (fileId, name) =>
    set(
      ({ delivery }) => {
        const fileExists = delivery.uploadedDocuments.some(
          (file) => file.id === fileId
        );
        if (!fileExists) {
          throw new Error("Filen finns inte tillgänglig i den aktuella vyn.");
        }

        const updatedFiles = delivery.uploadedDocuments.map((file) =>
          file.id === fileId ? { ...file, name } : file
        );

        return {
          delivery: {
            ...delivery,
            uploadedDocuments: updatedFiles
          }
        };
      },
      false,
      "DELIVERY_FILE_UPDATE_NAME"
    ),
  setHandled: (fileId: string, handledById: string, handledByName: string) =>
    set(
      ({ delivery }) => {
        const uploadedDocuments = [...delivery.uploadedDocuments];
        const updatedUploadedDocuments = uploadedDocuments.map((doc) =>
          doc.id === fileId
            ? {
                ...doc,
                checked: false,
                handled: true,
                handledById,
                handledByName,
                handledAt: new Date()
              }
            : doc
        );
        return {
          delivery: {
            ...delivery,
            uploadedDocuments: updatedUploadedDocuments
          }
        };
      },
      false,
      "delivery/FILES_SET_HANDLED"
    ),
  fetchUploadedFiles: async (customerId: string) => {
    get().delivery.gettingUploadedDocuments();
    try {
      const items = await DeliveryAPI.fetchUploadedFiles(customerId);
      get().delivery.getUploadedDocuments(items);
    } catch (error) {
      get().error.appendErrorNotification(
        "Hämtning av uppladdade filer misslyckades.",
        error
      );
    } finally {
      get().delivery.failedToGetUploadedDocuments();
    }
  },
  setFileAsHandled: async (itemToHandle: DeliveryFile, customerId: string) => {
    const userInfo = get().user.userInfo;
    if (!userInfo) {
      throw new Error("Användaruppgifter saknas eller kunde inte läsas in.");
    }

    try {
      await DeliveryAPI.setFileAsHandled(itemToHandle, customerId);

      get().delivery.setHandled(
        itemToHandle.id,
        userInfo.id,
        userInfo.givenName + " " + userInfo.surName
      );

      get().status.setUnhandledUploadsStatus(customerId);
    } catch (error) {
      get().error.appendErrorNotification(
        "Markering av filen som hanterad misslyckades.",
        error
      );
    }
  },
  moveToArchive: async (
    item: ArchiveItem | DeliveryFile,
    customerId: string,
    path: string,
    name: string
  ) => {
    try {
      const userInfo = get().user.userInfo;

      if (!userInfo) {
        throw new Error("Användaruppgifter saknas eller kunde inte läsas in.");
      }

      get().delivery.fileMoved(
        item.id,
        userInfo.id,
        userInfo.givenName + " " + userInfo.surName,
        name
      );
      await DeliveryAPI.moveToArchive(item, customerId, path, name);
    } catch (error) {
      get().error.appendErrorNotification(
        "Filöverföringen till dokumentarkivet misslyckades.",
        error
      );
    }
  },
  sendFile: async (
    file,
    customerId,
    setFiles,
    setFailedWrapper: (isFailed: boolean, message: string) => boolean
  ) => {
    try {
      const newItem = await DeliveryAPI.sendFile(file, customerId);
      get().delivery.filesUploaded([newItem]);

      setFiles((prevFiles) =>
        prevFiles.filter((f) => f.meta.name !== newItem.name)
      );
    } catch (error: any) {
      const MAX_FILE_SIZE = 4.4 * 1024 * 1024;
      if (file.file.size > MAX_FILE_SIZE) {
        setFailedWrapper(true, "FILE_TOO_LARGE_DELIVERY");
        return;
      }
      try {
        const result = JSON.parse(error.response.text);
        const errors = result.errors;

        Object.keys(errors).forEach((key) => {
          setFailedWrapper(true, errors[key][0]);
        });
      } catch (parseError) {
        get().error.appendErrorNotification(
          "Filöverföringen misslyckades.",
          "Misslyckades med att tolka felresponsen"
        );
      }
    } finally {
      get().delivery.failedToUploadFiles();
    }
  },
  deleteDocuments: async (customerId, fileId) => {
    const { appendErrorNotification } = get().error;

    if (!customerId.trim()) {
      appendErrorNotification(
        "Kund-ID är okänt",
        new Error("Kund-ID är okänt")
      );
      return;
    }

    if (!fileId?.trim()) {
      appendErrorNotification("Fil-ID är okänt", new Error("Fil-ID är okänt"));
      return;
    }

    try {
      await DeliveryAPI.deleteDocuments(customerId, fileId);
    } catch (error) {
      appendErrorNotification("Borttagning av filerna misslyckades.", error);
    }
  }
});
